import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useI18n } from '@locales/client';
import * as Dialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

import {
  BodyTextBoldPrimary,
  BodyTextBoldSecondary,
  BodyTextRegularSecondary,
  HeadingRegularSenary,
} from '@/shared/ui/typography';
import { useCurrency } from '@/entities/currency/model/hooks';
import { Currency } from '@/entities/currency/ui/Currency';

import CaretRight from '@/shared/assets/caret-right.svg';
import { Condition } from '@/shared/ui/condition';
import { Box } from '@/shared/ui/box';
import { useUser } from '@/shared/providers/user-provider/use-user';
import { Image } from '@/shared/ui/image';

import { useFiatForm } from '@/features/wallet-managment/model/hooks';
import {
  WalletSkeleton,
  WalletHeader,
  WalletTabs,
  BackButton,
  WithdrawFiatForm,
  WithdrawForm,
  HistoryDetail,
  History,
  DepositAddress,
  FiatForm,
} from '@/features/wallet-managment/ui';
import { useConfirmDataPrompt } from '@/features/data-managment/model/hooks';
import {
  CloseIconImage,
  StyledAlertDialogOverlay,
  StyledAlertDialogContent,
} from '@/features/auth-managment/styles';
import { useNotifications } from '@/shared/providers/notifications-provider/notifications-context';

import {
  CurrencyContainer,
  CurrencyWrapper,
  PaymentBlock,
  Wrapper,
  BonusWrapper,
  BlockStatic,
} from './styles';

const WalletManagment = ({ onClose, visible, onOpen }) => {
  const { currencies, isCurrenciesLoading } = useCurrency();
  const { removeQueryParam } = useConfirmDataPrompt();
  const [currentCurrency, setCurrentCurrency] = useState(null);
  const [currentTransaction, setCurrentTransaction] = useState(null);
  const [currentStep, setCurrentStep] = useState('deposit');
  const { user, setUser } = useUser();
  const { notifications, setNotifications } = useNotifications();
  const {
    paymentMethods,
    payoutMethods,
    isLoading: isSettingsLoading,
    trigger: createDepositFiatTrigger,
  } = useFiatForm({
    bonus: user?.userDepositBonus,
    visible,
  });
  const [bonusActive, setBonusActive] = useState(false);
  const [currentMethod, setCurrentMethod] = useState(null);
  const [hasBonusPopup, setHasBonusPopup] = useState(false);

  const t = useI18n();
  const searchParams = useSearchParams();
  const isModalConfirm = searchParams.get('modal') === 'confirm';
  const isOpen = visible && !isModalConfirm;
  const welcomeBonus = sessionStorage.getItem('welcomeBonus');

  const handleClose = () => {
    setCurrentStep('deposit');
    removeQueryParam('openPayment');
    setNotifications({ ...notifications, modal: null });
    onClose();
  };

  const handleBackButton = (key: string) => () => {
    setCurrentCurrency(null);
    setCurrentStep(key);
    removeQueryParam('openPayment');
  };

  useEffect(() => {
    const withdraw = searchParams.get('withdraw');
    const deposit = searchParams.get('deposit');
    const openPayment = searchParams.get('openPayment');

    if (withdraw || deposit || openPayment) {
      onOpen();
    }
    if (withdraw) {
      setCurrentStep('withdraw');
    } else if (openPayment) {
      setCurrentStep('history-detail');
    }
  }, [searchParams]);

  useEffect(() => {
    if (welcomeBonus) {
      onOpen();
      sessionStorage.removeItem('welcomeBonus');
    }
  }, [welcomeBonus]);

  useEffect(() => {
    if (user.isBonusDepositReplenish) {
      onOpen();
      setHasBonusPopup(true);
      setUser({
        ...user,
        isBonusDepositReplenish: false,
      });
    }

    if (user.userDepositBonus) {
      setBonusActive(true);
    }
  }, [user.isBonusDepositReplenish, user.userDepositBonus]);

  const renderCurrencyContent = (methods, isDeposit) => (
    <CurrencyContainer>
      {methods?.methods.map((method, index, array) => {
        const isLast = array[array.length - 1].method === method.method;
        return (
          <PaymentBlock
            key={method.method}
            $isFirst={index === 0}
            $isLast={isLast}
            onClick={() => {
              setCurrentMethod(method);
              setCurrentStep(
                isDeposit ? 'fiat-form' : `withdraw-fiat-form-${method.method}`
              );
            }}
          >
            <Box $display="flex" $gap={10} $alignItems="center">
              {method.icon && (
                <Image src={method.icon} width={36} height={36} alt="icon" />
              )}
              <Box $textAlign="left">
                <BodyTextBoldPrimary>
                  {method?.name ? method?.name : '-'}
                </BodyTextBoldPrimary>
              </Box>
            </Box>
            <CaretRight />
          </PaymentBlock>
        );
      })}

      <CurrencyWrapper>
        {currencies.length === 0 ? (
          <Box
            $fill
            $display="grid"
            $justifyContent="center"
            $alignContent="center"
          >
            <BodyTextBoldSecondary>
              {t('common.filter.provider.nothing')}
            </BodyTextBoldSecondary>
          </Box>
        ) : (
          currencies.map((currency) => (
            <Currency
              key={currency.code}
              currency={currency}
              onClick={() => {
                setCurrentCurrency(currency);
                setCurrentStep(isDeposit ? 'address-detail' : 'withdraw-form');
              }}
            />
          ))
        )}
      </CurrencyWrapper>
    </CurrencyContainer>
  );

  if (isCurrenciesLoading || isSettingsLoading) {
    return (
      <Dialog.Root open={visible} onOpenChange={onOpen}>
        <Dialog.Portal>
          <VisuallyHidden>
            <Dialog.Title>WalletManagment</Dialog.Title>
          </VisuallyHidden>
          <StyledAlertDialogOverlay onClick={handleClose} />
          <StyledAlertDialogContent>
            <Dialog.Title />
            <WalletSkeleton t={t} />
          </StyledAlertDialogContent>
        </Dialog.Portal>
      </Dialog.Root>
    );
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={onOpen}>
      <Dialog.Portal>
        <VisuallyHidden>
          <Dialog.Title>WalletManagment</Dialog.Title>
        </VisuallyHidden>
        <StyledAlertDialogOverlay onClick={handleClose} />
        <StyledAlertDialogContent $overflow={'hidden'}>
          <Wrapper
            $isSticky={currentStep === 'history'}
            $isFiat={currentStep === 'fiat-form'}
            $isFiatWithdraw={currentStep.startsWith('withdraw-fiat-form-')}
          >
            <Condition match={currentStep === 'deposit'}>
              <BlockStatic $hasBonusPopup={hasBonusPopup}>
                <WalletHeader
                  onClose={handleClose}
                  balance={notifications?.balance}
                  user={user}
                />

                <Condition match={hasBonusPopup}>
                  <BonusWrapper>
                    <HeadingRegularSenary color="#08AD5E" $marginBottom={8}>
                      {t('wallet.bonus_popup.title')}
                    </HeadingRegularSenary>
                    <BodyTextRegularSecondary>
                      {t('wallet.bonus_popup.description')}
                    </BodyTextRegularSecondary>
                  </BonusWrapper>
                </Condition>

                <WalletTabs
                  activeTab="deposit"
                  setCurrentStep={setCurrentStep}
                  t={t}
                />
              </BlockStatic>

              {renderCurrencyContent(paymentMethods, true)}
            </Condition>

            <Condition match={currentStep === 'withdraw'}>
              <BlockStatic>
                <WalletHeader
                  onClose={handleClose}
                  balance={notifications.balance}
                  user={user}
                />
                <WalletTabs
                  activeTab="withdraw"
                  setCurrentStep={setCurrentStep}
                  t={t}
                />
              </BlockStatic>
              {renderCurrencyContent(payoutMethods, false)}
            </Condition>

            <Condition match={currentStep === 'history'}>
              <BlockStatic>
                <WalletHeader
                  onClose={handleClose}
                  balance={notifications.balance}
                  user={user}
                />
                <WalletTabs
                  activeTab="history"
                  setCurrentStep={setCurrentStep}
                  t={t}
                />
              </BlockStatic>
              <CurrencyContainer>
                <History
                  setCurrentStep={setCurrentStep}
                  currencies={currencies}
                  setCurrentTransaction={setCurrentTransaction}
                />
              </CurrencyContainer>
            </Condition>

            <Condition match={currentStep === 'withdraw-form'}>
              <Box onClick={handleClose}>
                <CloseIconImage
                  width={32}
                  height={32}
                  src="/images/icons/close-dialog.svg"
                />
              </Box>
              <BackButton onClick={handleBackButton('withdraw')} t={t} />
              <WithdrawForm
                currency={currentCurrency}
                settings={payoutMethods}
                currentStep={currentStep}
                setCurrentTransaction={setCurrentTransaction}
                setCurrentStep={setCurrentStep}
              />
            </Condition>

            <Condition match={currentStep === 'fiat-form'}>
              <Box onClick={handleClose}>
                <CloseIconImage
                  width={32}
                  height={32}
                  src="/images/icons/close-dialog.svg"
                />
              </Box>
              <BackButton onClick={handleBackButton('deposit')} t={t} />
              <FiatForm
                bonusActive={bonusActive}
                setBonusActive={setBonusActive}
                settings={currentMethod}
                createDepositFiatTrigger={createDepositFiatTrigger}
                currentBonus={paymentMethods?.bonus}
              />
            </Condition>

            <Condition match={currentStep === 'history-detail'}>
              <Box onClick={handleClose} $zIndex="1">
                <CloseIconImage
                  width={32}
                  height={32}
                  src="/images/icons/close-dialog.svg"
                />
              </Box>
              <BackButton onClick={handleBackButton('history')} t={t} />
              <HistoryDetail
                notifications={notifications}
                transaction={currentTransaction}
              />
            </Condition>

            <Condition match={currentStep === 'address-detail'}>
              <Box onClick={handleClose}>
                <CloseIconImage
                  width={32}
                  height={32}
                  src="/images/icons/close-dialog.svg"
                />
              </Box>
              <BackButton onClick={handleBackButton('deposit')} t={t} />
              <DepositAddress
                bonus={user.userDepositBonus}
                bonusActive={bonusActive}
                setBonusActive={setBonusActive}
                currentCurrency={currentCurrency}
              />
            </Condition>

            <Condition match={currentStep.startsWith('withdraw-fiat-form-')}>
              <WithdrawFiatForm
                setCurrentTransaction={setCurrentTransaction}
                settings={payoutMethods}
                onClose={handleClose}
                setCurrentCurrency={setCurrentCurrency}
                setCurrentStep={setCurrentStep}
                currentStep={currentStep}
              />
            </Condition>
          </Wrapper>
        </StyledAlertDialogContent>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export { WalletManagment };
